import { Component, JSX, ParentProps, ValidComponent, createMemo, onMount, splitProps } from 'solid-js'
import styles from './st-flexbox.module.css'
import { Dynamic } from 'solid-js/web';

type AsType<T extends ValidComponent> = {
    as?: T
}

// helper to grab the right props for either an intrinsic tag or a custom component
type PropsOf<T extends ValidComponent> =
    // if T is a string tag name, grab its intrinsic attributes
    T extends keyof JSX.IntrinsicElements
        ? Omit<JSX.IntrinsicElements[T], 'dir'>
        // else if T is a Solid Component, pull its props
        : T extends Component<infer P>
            ? P
            : Record<string, unknown>

export class FlexboxProps {
    wrap?= false;
    middle?= false;
    padded?= false;
    auto?= false;
    gap?= '';
    dir : 'horizontal' | 'vertical' = 'horizontal'
}

export default function Flexbox<T extends ValidComponent = 'div'>(props: ParentProps<FlexboxProps> & AsType<T> & PropsOf<T>) {
    const [local, other] = splitProps(props, ['class', 'as', 'wrap', 'middle', 'padded', 'auto', 'dir', 'gap'] as const)
    const flexClasses = createMemo(() => {
        const res: string[] = [];

        if (props.class) {
            res.push(props.class);
        }

        res.push(styles.flexbox);
        res.push(styles[props.dir]);

        if (props.auto) {
            res.push(styles.auto);
        }

        if (props.middle) {
            res.push(styles.middle);
        }

        if (props.padded) {
            res.push(styles.padded);
        }

        if (props.wrap) {
            res.push(styles.wrap);
        }

        return res.join(' ');
    });

    let el!: HTMLElement;
    onMount(() => {
        if (props.gap) {
            el.style.gap = props.gap;
        }
    })

    return <Dynamic
        ref={el}
        component={props.as ?? 'div'}
        class={flexClasses()}
        {...other}>
        {props.children}
    </Dynamic>
}

type FlexboxItemProps = {
    auto? : boolean
};
Flexbox.Item = function FlexboxItem<T extends ValidComponent = 'div'>(props: ParentProps<FlexboxItemProps> & AsType<T> & PropsOf<T>) {
    const [local, other] = splitProps(props, ['class', 'as', 'auto'] as const)
    const itemClasses = createMemo(() => {
        const res: string[] = [];

        if (props.class) {
            res.push(props.class);
        }

        if (props.auto) {
            res.push(styles.auto);
        }

        return res.join(' ');
    });

    return <Dynamic
        component={props.as ?? 'div'}
        class={itemClasses()}
        {...other}>
        {props.children}
    </Dynamic>
}

Flexbox.defaultProps = new FlexboxProps();